import * as allTypes from 'constants/ActionTypes';
import { FETCH_DEVICE_SUCCESS, FETCH_DEVICES_SUCCESS } from 'modules/devices/actionTypes';
import * as interventionTypes from 'modules/interventions/actionTypes';
import * as leadsTypes from 'modules/leads/actionTypes';
import * as userGroupsTypes from 'modules/userGroups/actionTypes';

import { createReducer } from '../../../../utils/ReduxUtils';
import * as types from '../../actionTypes';

/**
 * @typedef { import("modules/users/types/projections/public-user").PublicUser |
 *            import('modules/users/types/projections/user').User |
 *            import('modules/users/types/projections/detailed-user').DetailedUser
 *          } UserDataByIdEntity
 * @typedef {{ [userId: string]: UserDataByIdEntity }} UserDataByIdReducer
 */

/** @type {UserDataByIdReducer} */
const initialState = {};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/users/types/actions').GetCurrentUserDataSuccessAction} action
 * @returns {UserDataByIdReducer}
 */
const userReducer = (state, { payload: user }) => ({ ...state, [user.id]: user });

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/users/types/actions').GetUsersSuccessAction} action
 * @returns {UserDataByIdReducer}
 */
const usersReducer = (state, { payload }) => {
  const data = payload.content.reduce((acc, user) => ({ ...acc, [user.id]: user }), {});
  return { ...state, ...data };
};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/dealers/types').FetchDealerSuccessAction} action
 * @returns {UserDataByIdReducer}
 */
const usersFromDealerReducer = (state, { payload: dealer }) => {
  if (dealer?.steps) {
    const data = Object.values(dealer.steps).reduce(
      (users, steps) => ({
        ...users,
        ...steps.filter(step => step?.user).reduce((acc, step) => ({ ...acc, [step.user.id]: step.user }), {}),
      }),
      {}
    );
    return { ...state, ...data };
  }
  return state;
};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/global/generic.types').Action<Array<{users?: Array<import('modules/users/types/projections/user').User>} & import('modules/userGroups/types/public-user-group').PublicUserGroup>>} action
 * @returns {UserDataByIdReducer}
 */
const usersFromUserGroupsReducer = (state, { payload: userGroups }) => {
  const data = userGroups
    .filter(userGroup => userGroup?.users?.length > 0)
    .flatMap(userGroup => userGroup.users)
    .reduce((acc, user) => ({ ...acc, [user.id]: user }), {});
  return { ...state, ...data };
};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/leads/types').FetchLeadsSuccessAction} action
 * @returns {UserDataByIdReducer}
 */
const usersFromLeadsReducer = (state, { payload }) => {
  const users = payload.content.reduce((acc, { assignee }) => {
    if (assignee) {
      acc.push(assignee);
    }
    return acc;
  }, []);

  return usersReducer(state, { payload: { content: users } });
};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/leads/types').LeadSuccessAction} action
 * @returns {UserDataByIdReducer}
 */
const usersFromLeadReducer = (state, { payload }) => {
  if (payload.lead.assignee) {
    /** @type {UserDataByIdEntity} */
    const { links: _links, ...user } = payload.lead.assignee;

    return {
      ...state,
      [user.id]: {
        ...state[user.id],
        ...user,
      },
    };
  }
  return state;
};

/**
 * @param {UserDataByIdReducer} state
 * @param {import('modules/global/generic.types').Action<import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention>} action
 * @returns {UserDataByIdReducer}
 */
const usersFromEntityReducerFactory = (fields = ['user']) => (state, { payload }) => {
  const newState = { ...state };

  fields.forEach(fieldName => {
    const user = payload[fieldName];

    if (user?.id) {
      newState[user.id] = {
        ...newState[user.id],
        ...user
      };
    }
  });

  return newState;
};

const usersFromEntitiesReducerFactory = (fields = ['user']) => {
  const entityReducer = usersFromEntityReducerFactory(fields);

  return (state, { payload } = {}) => {
    const { content } = payload;

    return content?.reduce(
      (acc, entity) => entityReducer(acc, { payload: entity }),
      state
    ) ?? state;
  };
};

export default createReducer(initialState, {
  [types.FETCH_USERS_SUCCESS]: usersReducer,
  [allTypes.FETCH_USER_SUCCESS]: userReducer,
  [types.CURRENT_USER_DATA_SUCCESS]: userReducer,
  [leadsTypes.ASSIGN_SUCCESS]: usersFromLeadReducer,
  [leadsTypes.FETCH_SUCCESS]: usersFromLeadsReducer,
  [allTypes.FETCH_DEALER_SUCCESS]: usersFromDealerReducer,
  [userGroupsTypes.FETCH_USER_GROUPS_SUCCESS]: usersFromUserGroupsReducer,
  [FETCH_DEVICE_SUCCESS]: usersFromEntityReducerFactory(),
  [FETCH_DEVICES_SUCCESS]: usersFromEntitiesReducerFactory(),
  [interventionTypes.FETCH_INTERVENTION_SUCCESS]: usersFromEntityReducerFactory(['createdBy', 'assignedUser', 'serviceAdvisorUser']),
  [interventionTypes.INTERVENTIONS_FETCH_SUCCESS]: usersFromEntitiesReducerFactory(['createdBy', 'assignedUser', 'serviceAdvisorUser']),
});
